import React from "react";
import { Box, useTheme, Drawer, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, Outlet } from "react-router-dom";
import SideBarNavigation from "../components/navigation/SideBarNavigation";
import TopBarNavigation from "../components/navigation/TopBarNavigation";
import Workspace from "../components/workspace/Workspace";
import useRouteToWorkspaceSync from "../hooks/useRouteToWorkspaceSync";
import { STANDALONE_ROUTES } from "../config/standAlonePagesRoutes";
import Footer from "../components/Footer";

const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 72;

const AppWrapper = () => {
  const theme = useTheme();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.user);
  const { isSidebarCollapsed } = useSelector((state) => state.ui);

  const [isMobileNavOpen, setMobileNavOpen] = React.useState(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const showSidebar = Boolean(userInfo);
  const currentSidebarWidth = isSidebarCollapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;

  useRouteToWorkspaceSync();

  const isStandalonePage = STANDALONE_ROUTES.some((route) =>
    location.pathname.startsWith(route),
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
      }}
    >
      {/* 1. SIDEBAR */}
      {showSidebar &&
        (isDesktop ? (
          <Box
            sx={{
              width: currentSidebarWidth,
              flexShrink: 0,
              height: "100%",
              borderRight: 1,
              borderColor: "divider",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }}
          >
            <SideBarNavigation />
          </Box>
        ) : (
          <Drawer
            anchor="left"
            open={isMobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            PaperProps={{ sx: { width: SIDEBAR_WIDTH_EXPANDED } }}
            ModalProps={{ keepMounted: true }}
          >
            <SideBarNavigation onMobileClose={() => setMobileNavOpen(false)} />
          </Drawer>
        ))}

      {/* 2. MAIN LAYOUT COLUMN */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* A. TopBar */}
        <Box sx={{ flexShrink: 0 }}>
          <TopBarNavigation onMobileNavOpen={() => setMobileNavOpen(true)} />
        </Box>

        {/* B. WORKSPACE (Replaces Outlet) */}
        <Box
          component="main"
          sx={{
            flex: 1,
            overflow: "hidden", // Let Workspace manage scrolling inside panels
            position: "relative",
            bgcolor:
              theme.palette.mode === "dark" ? "background.default" : "#f4f6f8",
          }}
        >
          {isStandalonePage ? (
            <Box sx={{ height: "100%", overflowY: "auto" }}>
              <Outlet />
            </Box>
          ) : (
            <Workspace />
          )}
        </Box>
        {/* C. Footer */}
        <Box
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

export default AppWrapper;
